import {Requestor} from 'hegyd/core/resources/assets/js/utils/Requestor.js';

export default class SearchProducts extends HTMLDivElement {
    constructor() {
        super();
        this.form = this.querySelector('form');
        this.input = this.form.querySelector('input[type="search"]');

        /**
         * Des événements externes à écouter peuvent être indiqués à travers un objet json passé en paramètre
         * pour provoquer la fermeture des résultats de recherche.
         * Exemple : pour fermer les résultats via les événements 'click' et 'blur' sur un élément '#selector1',
         *   et via les événements 'click' sur un autre élément '#selector2', on peut ajouter l'attribut HTML
         *   data-closing-events='{"#selector1":["click","blur"],"#selector2":["click"]}'
         */
        this.closingEvents = JSON.parse(this.dataset.closingEvents ?? 'null');
    }

    connectedCallback() {
        this.watch();
    }

    disconnectedCallback() {
        this.unwatch();
    }

    /**
     * Écoute les événements.
     */
    watch() {
        // Écoute tous les changements sur this.input.value
        this.input.addEventListener('input', this.processSearchTerm.bind(this));

        // Si la fenêtre est redimensionnée
        window.addEventListener('resize', this.clearResults.bind(this));

        // Déclare dynamiquement les événements qui doivent occasionner la fermeture du volet de recherche
        if (this.closingEvents && typeof this.closingEvents === 'object') {
            for (const [selector, events] of Object.entries(this.closingEvents)) {
                const el = document.querySelector(selector);
                if ( ! el) { continue; }
                for (const event of events) {
                    if (typeof event === 'string') {
                        el.addEventListener(event, this.clearResults.bind(this));
                    }
                }
            }
        }
    }

    /**
     * Désactive les écouteurs d'événements externes.
     */
    unwatch() {
        window.removeEventListener('resize', this.clearResults);

        if (this.closingEvents && typeof this.closingEvents === 'object') {
            for (const [selector, events] of Object.entries(this.closingEvents)) {
                const el = document.querySelector(selector);
                if ( ! el) { continue; }
                for (const event of events) {
                    if (typeof event === 'string') {
                        el.removeEventListener(event, this.clearResults);
                    }
                }
            }
        }
    }

    /**
     * Gère le lancement de la recherche.
     */
    processSearchTerm() {
        this.debounce(() => {
            if (this.input.value === '') {
                this.clearResults();
            }

            this.search();
        })();
    }

    /**
     * Effectue la recherche.
     */
    search() {
        if (this.input.value.length < 3) {
            this.clearResults();
            
            return;
        }

        const requestor = new Requestor();
        const data = {};
        
        data[this.input.name] = this.input.value;
        requestor.sendRequest(
            this.form.action,
            this.form.method ? this.form.method : 'GET',
            data,
            this.searchSuccessCallback.bind(this)
        );
    }

    /**
     * Gère l'affichage des résultats de recherche.
     * @param data Résultat de la requête AJAX
     */
    searchSuccessCallback(data) {
        this.clearResults();

        /*
         * Pour accéder au HTML de la réponse comme élément du DOM
         * et en faire un attribut de this, on crée un élément englobant,
         * dont on viendra ensuite récupérer le premier enfant.
         */
        const resultsContainer = document.createElement('div');
        resultsContainer.innerHTML = data.template;
        this.results = resultsContainer.firstElementChild;

        // On peut alors injecter l'élément créé à partir des résultats
        this.appendChild(this.results);
        this.results.style.setProperty('--input-bottom-position', this.input.getBoundingClientRect().bottom + "px");
        this.results.style.setProperty('--input-left-position', this.input.getBoundingClientRect().left + "px");
        this.results.style.setProperty('--input-width', this.input.getBoundingClientRect().width + "px");

        // Événement de fermeture au clic sur l'overlay
        this.results.addEventListener('click', this.considerClosingResults.bind(this))

        // Crée un événement qui pourra être utilisé par d'autres composants
        this.input.dispatchEvent(new Event('search.opened'));
    }

    /**
     * Ferme les résultats de recherche.
     */
    clearResults() {
        if (this.results) {
            // Supprime les résultats du DOM
            this.results.remove();

            // Supprime l'événement de fermeture
            this.results.removeEventListener('click', this.considerClosingResults);

            // Crée un événement qui pourra être utilisé par d'autres composants
            this.input.dispatchEvent(new Event('search.closed'));
        }
    }

    /**
     * Callback d'événement appelé au clic sur les résultats de recherche.
     * Si le clic est fait sur l'overlay, on ferme la recherche.
     */
    considerClosingResults(event) {
        if (event.target.classList.contains('search-overlay')) {
            this.clearResults();
        }
    }

    debounce(func, timeout = 300) {
        return (...args) => {
            clearTimeout(this.timer);
            this.timer = setTimeout(() => {
                func.apply(this, args);
            }, timeout);
        };
    }
}
